import React, { useMemo, useState } from "react";

import { Fade, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import orderBy from "lodash/orderBy";
import { FormProvider, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

import useGetFartherManagedAccounts from "@fartherfinance/frontend/api/Accounts/hooks/useGetFartherManagedAccounts";
import { FartherManagedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import useGetRaiseCash from "@fartherfinance/frontend/api/Liquidations/hooks/useGetRaiseCash";
import useAllAccountBalancesV4 from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalancesV4";
import { ClientId, FartherAccountId } from "@fartherfinance/frontend/api/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import Checkbox from "@src/sharedComponents/Checkbox/Checkbox";
import FlexWrapper from "@src/sharedComponents/Forms/FlexWrapper";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import Spacer from "@src/sharedComponents/Forms/Spacer";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import TooltipBasic from "@src/sharedComponents/Tooltip/TooltipBasic";
import Text from "@src/sharedComponents/Typography/Text";
import {
  formNumberFieldUpTo2DecimalsWithCommasCustomRegex,
  formNumberFieldWithCommasRules,
} from "@src/utils/formValidation";
import toUSD from "@src/utils/toUSD";

import RaiseCashWarningModal from "./RaiseCashWarningModal";

import styles from "./RaiseCash.module.css";

const AccountLabel = "AccountLabel";
const AmountLabel = "AmountLabel";

interface Form {
  [AccountLabel]: { label: string; value: FartherAccountId } | undefined;
  [AmountLabel]: string;
}

interface Props {
  accounts: FartherManagedAccount[];
  onClose: () => void;
  onContinue: (
    accountId: FartherAccountId,
    amount: string,
    isFullLiquidation: boolean
  ) => void;
}

const RaiseCashForm: React.FC<Props> = ({ accounts, onClose, onContinue }) => {
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [isFullLiquidation, setIsFullLiquidation] = useState<boolean>(false);

  const { clientId } = useParams<{ clientId: ClientId }>();

  const methods = useForm<Form>({
    mode: "onChange",
    defaultValues: {
      [AccountLabel]: undefined,
      [AmountLabel]: "",
    },
  });

  const {
    getValues,
    formState: { isValid, isDirty },
  } = methods;

  const auth = useRequestAuth();
  const doNotIncludeSharedAccounts = true;
  const balances = useAllAccountBalancesV4(
    clientId,
    doNotIncludeSharedAccounts,
    auth
  );
  const fartherAccounts = useGetFartherManagedAccounts(clientId, auth);
  const raiseCashRequests = useGetRaiseCash(clientId, {}, auth);

  const accountOptions = useMemo(
    () =>
      orderBy(
        accounts.map((account) => ({
          label: `${account.accountDetails.displayName} (${
            account.accountDetails.custodianAccountNumber ??
            "No Custodian Account Number"
          })`,
          value: account.virtualAccountId,
        })),
        [(a) => a.label.toLowerCase()],
        ["asc"]
      ),
    [accounts]
  );

  const handleOnContinue = () => {
    const values = getValues();

    if (values[AccountLabel] === undefined) {
      return;
    }

    if (isFullLiquidation) {
      if (balances.isLoading || balances.hasError) {
        return;
      }

      const accountId = values[AccountLabel].value;
      const balanceInfo = balances.data.find((b) => b.accountId === accountId);
      if (balanceInfo === undefined) {
        throw new Error(
          `Account balance not found for accountId: ${accountId}`
        );
      }
      const amount = balanceInfo.balance;
      if (amount === null) {
        throw new Error(
          `Account balance should not be null for accountId: ${accountId}`
        );
      }

      onContinue(values[AccountLabel].value, `${amount}`, true);
      return;
    }

    onContinue(values[AccountLabel].value, values[AmountLabel], false);
  };

  const isDisabled = useMemo(() => {
    const values = getValues();

    if (isFullLiquidation === false) {
      if (values[AmountLabel] === undefined || values[AmountLabel] === "") {
        return true;
      }

      return isValid === false || isDirty === false;
    }

    return values[AccountLabel] === undefined;
  }, [getValues, isValid, isDirty, isFullLiquidation]);

  const raiseCashInProgress = useMemo(() => {
    if (fartherAccounts.isLoading || fartherAccounts.hasError) {
      return [];
    }

    if (raiseCashRequests.isLoading || raiseCashRequests.hasError) {
      return [];
    }

    const inProgress = (raiseCashRequests.data?.data ?? []).filter(
      (rc) =>
        rc.status === "Initiated" ||
        rc.status === "PendingLiquidationProposalApproval" ||
        rc.status === "PendingLiquidationTaskCompletion"
    );

    return inProgress.map((rc) => {
      const account = fartherAccounts.data?.find(
        (a) => a.virtualAccountId === rc.accountId
      );
      if (account === undefined) {
        throw new Error(
          `Account not found from list of raise cash requests in progress for accountId: ${rc.accountId}`
        );
      }
      if (rc.amount === null) {
        throw new Error(
          `Raise cash request amount should not be null for accountId: ${rc.accountId}`
        );
      }

      return {
        accountId: rc.accountId,
        accountName: account.accountDetails.displayName,
        custodianAccountNumber:
          account.accountDetails.custodianAccountNumber ??
          "No Custodian Account Number",
        amount: parseFloat(rc.amount),
        isFullLiquidation: rc.isFullLiquidation,
        createdAt: rc.createdAt,
      };
    });
  }, [raiseCashRequests, fartherAccounts]);

  return (
    <>
      {balances.isLoading && <LogoLoadingStill onTop={true} />}

      {showWarningModal && (
        <RaiseCashWarningModal
          onClose={() => setShowWarningModal(false)}
          onContinue={handleOnContinue}
        />
      )}

      <Fade in>
        <Stack>
          <Typography className={styles.title}>
            Make a request to raise cash
          </Typography>

          <Typography className={styles.description}>
            Prior to making a request to raise cash, please ensure that the
            account has a model portfolio selected and trading is turned on. If
            this is a retirement account, please request an amount which is
            sufficient to cover any tax obligations.
          </Typography>

          {raiseCashInProgress.length > 0 && (
            <>
              <Spacer verticalSpacing="32px" />

              <div className={styles.flexRow}>
                <Text variant={"title3"}>Requests In Progress</Text>

                <TooltipBasic
                  className={styles.tooltip}
                  type={"secondary"}
                  placement={"right"}
                  text={
                    "The following accounts have requests to raise cash in progress. In order to make a new raise cash request for an account that already has a request in progress you must first cancel the existing request or wait for it to complete."
                  }
                />
              </div>

              <Spacer verticalSpacing="8px" />

              {raiseCashInProgress.map((r) => (
                <div key={r.accountId} className={styles.inProgressRow}>
                  <Text variant={"body"}>{`${r.accountName}`}</Text>

                  <Text
                    className={styles.textWithSpacing1}
                    variant={"body"}
                    color={"textSubtle"}
                  >
                    {`(${r.custodianAccountNumber})`}
                  </Text>

                  <Text variant={"body"}>{toUSD(r.amount)}</Text>

                  {r.isFullLiquidation && (
                    <Text
                      className={styles.textWithSpacing3}
                      variant={"body"}
                      color={"textSubtle"}
                    >
                      {"(Full Liquidation)"}
                    </Text>
                  )}

                  <Text
                    className={styles.textWithSpacing2}
                    variant={"body"}
                    color={"textSubtle"}
                  >
                    {`on ${format(r.createdAt, "M/d/yyyy")}`}
                  </Text>
                </div>
              ))}

              <Spacer verticalSpacing="16px" />
            </>
          )}

          <Spacer verticalSpacing="10px" />

          {balances.hasError === false && (
            <>
              <Spacer verticalSpacing="20px" />

              <div className={styles.flexRow}>
                <Checkbox
                  checked={isFullLiquidation}
                  onChange={() => setIsFullLiquidation(!isFullLiquidation)}
                  label={"Full Liquidation"}
                  disabled={balances.isLoading}
                />

                <TooltipBasic
                  className={styles.tooltip2}
                  type={"secondary"}
                  placement={"right"}
                  text={
                    "Assets marked as Do Not Sell will not be sold in a full liquidation."
                  }
                />
              </div>
            </>
          )}

          <FormProvider {...methods}>
            <FlexWrapper>
              <FormDropdownField
                name={AccountLabel}
                label="Account"
                values={accountOptions}
                required="Please select an account"
              />

              {isFullLiquidation === false && (
                <FormNumberField
                  name={AmountLabel}
                  label="Amount"
                  labelTooltip="Security sales associated with Raise Cash requests do not include cash buffers implied by the model portfolio. As such, many accounts are expected to rebalance again following a Raise Cash request to refill the cash buffer."
                  required="Please specify an amount to raise cash"
                  startAdornment="$"
                  customRegex={
                    formNumberFieldUpTo2DecimalsWithCommasCustomRegex
                  }
                  rules={formNumberFieldWithCommasRules({
                    required: true,
                    minValue: 0,
                    maxValue: 1_000_000_000,
                  })}
                  autoAddCommas={true}
                />
              )}
            </FlexWrapper>
          </FormProvider>

          <Spacer verticalSpacing="40px" />

          <Stack direction="row" gap="16px" alignSelf="flex-end">
            <Button
              variant="outlined"
              buttonType="primary"
              text="Cancel"
              onClick={onClose}
            />

            <Button
              variant="contained"
              buttonType="primary"
              text="Continue"
              disabled={isDisabled}
              onClick={() => setShowWarningModal(true)}
            />
          </Stack>
        </Stack>
      </Fade>
    </>
  );
};

export default RaiseCashForm;
